import { useEffect, useRef } from 'react';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { fetchExtendedTranscriptionInfo } from 'flow/actions/transcription-actions';
import { CallRecordKey, QueueKey } from 'flow/types/EntityKeys';

// 5s, 5s, 5s, 6s, 7s, 10s, then 15s for the rest
const POLL_DELAYS_MS = [5000, 5000, 5000, 6000, 7000, 10000];
const MAX_DELAY_MS = 15_000;
const MAX_POLL_COUNT = 20;

const getDelay = (attempt: number) => POLL_DELAYS_MS[attempt] ?? MAX_DELAY_MS;

export const usePollCallRecordWhilePending = (
    callRecordKey: CallRecordKey | undefined,
    transcriptionIsPending: boolean | undefined,
    callRecordQueueKey?: QueueKey,
) => {
    const dispatch = useAppDispatch();
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const attemptRef = useRef(0);

    useEffect(() => {
        if (!callRecordKey || !transcriptionIsPending) {
            clearTimeout(timeoutRef.current);
            attemptRef.current = 0;
            return undefined;
        }

        const poll = () => {
            if (attemptRef.current >= MAX_POLL_COUNT) return;

            const delay = getDelay(attemptRef.current);
            timeoutRef.current = setTimeout(() => {
                dispatch(
                    fetchExtendedTranscriptionInfo(
                        callRecordKey,
                        callRecordQueueKey,
                    ),
                );
                attemptRef.current += 1;
                poll();
            }, delay);
        };

        poll();

        return () => clearTimeout(timeoutRef.current);
    }, [callRecordKey, transcriptionIsPending, callRecordQueueKey, dispatch]);
};
